import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const Blog5 = () => {
    const navigate = useNavigate();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogData = blogs.find(b => b.id === 5);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            5 Car Dashboard Warning Lights You Should Never Ignore
                        </h1>

                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            Dashboard warning lights are your car’s way of telling you that something
                            needs attention. Some are minor reminders, but others can point to serious
                            safety or engine problems that should never be ignored.
                        </p>

                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated March 2026 &nbsp;|&nbsp; 9 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/5_1.jpeg"
                            alt="Car dashboard with important warning lights illuminated"
                            title="5 Car Dashboard Warning Lights You Should Never Ignore"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: dashboard_warning_lights_featured.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">
                            In this article
                        </div>

                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#warning-table" className="text-gray-900 no-underline hover:underline">Warning light summary</a></li>
                            <li><a href="#why-it-matters" className="text-gray-900 no-underline hover:underline">Why warning lights matter</a></li>
                            <li><a href="#light-1" className="text-gray-900 no-underline hover:underline">1. Check engine light</a></li>
                            <li><a href="#light-2" className="text-gray-900 no-underline hover:underline">2. Oil pressure warning light</a></li>
                            <li><a href="#light-3" className="text-gray-900 no-underline hover:underline">3. Battery warning light</a></li>
                            <li><a href="#light-4" className="text-gray-900 no-underline hover:underline">4. Brake warning light</a></li>
                            <li><a href="#light-5" className="text-gray-900 no-underline hover:underline">5. Engine temperature warning light</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">Understand warning lights with CarFido</a></li>
                            <li><a href="#extra-tips" className="text-gray-900 no-underline hover:underline">What to do when a warning light appears</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px]">
                            Most drivers have seen a dashboard warning light come on and hoped it was
                            nothing serious. Sometimes it really is something small, but sometimes
                            that little symbol is the only early warning you get before a breakdown, a
                            safety issue, or expensive engine damage.
                        </p>

                        <p className="mb-[18px]">
                            That is why it helps to know which warning lights deserve immediate
                            attention. If you understand what the symbol means and how urgent it may
                            be, you can make a better decision about whether to keep driving, pull
                            over, or schedule service right away.
                        </p>

                        <p className="m-0 text-gray-700">
                            This guide explains 5 car dashboard warning lights you should never
                            ignore, what each one usually means, and what to do next if you see one
                            on your dash.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>Which car dashboard warning lights should never be ignored?</strong>
                            <br />
                            Some of the most important warning lights include the check engine light,
                            oil pressure warning light, battery warning light, brake warning light,
                            and engine temperature warning light. These can point to problems that
                            affect safety, reliability, or major engine components.
                        </p>
                    </section>

                    <section id="warning-table" className="mb-10">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Dashboard Warning Light Summary
                        </h2>

                        <div className="overflow-x-auto border border-gray-200 rounded-xl">
                            <table className="w-full border-collapse bg-white text-sm">
                                <thead className="bg-gray-50 text-gray-900 font-bold">
                                    <tr>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Warning Light</th>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Why It Matters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium">Check engine light</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">
                                            Can signal engine, emissions, ignition, or sensor problems
                                        </td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium">Oil pressure warning light</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">
                                            Low oil pressure can quickly damage the engine
                                        </td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium">Battery warning light</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">May indicate charging system failure</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium">Brake warning light</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Can affect stopping safety</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 font-medium">
                                            Engine temperature warning light
                                        </td>
                                        <td className="py-3.5 px-4 text-gray-600">
                                            Overheating can cause serious engine damage
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="why-it-matters" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Why Dashboard Warning Lights Matter
                        </h2>

                        <p className="mb-4 text-gray-700">
                            Modern vehicles monitor many systems at the same time. When something is
                            outside the normal range, the dashboard often gives you an alert before
                            the problem becomes more serious. That early warning can help you fix a
                            small issue before it turns into a major repair bill.
                        </p>

                        <p className="mb-6 text-gray-700">
                            The problem is that many drivers become used to warning lights and delay
                            checking them. That can be risky. Some warning lights point to issues that
                            may damage the engine within minutes, while others can reduce braking
                            performance or leave the vehicle unable to start.
                        </p>

                        <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                            <li>They warn you about developing problems early</li>
                            <li>They can help prevent expensive mechanical damage</li>
                            <li>Some warning lights affect safety directly</li>
                            <li>Understanding urgency helps you respond more calmly and correctly</li>
                        </ul>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/5_2.jpeg"
                            alt="Dashboard with check engine warning light illuminated"
                            title="Check engine light warning"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: dashboard_check_engine_light.png
                        </figcaption>
                    </figure>

                    <section id="light-1" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            1. Check Engine Light
                        </h2>
                        <p className="mb-4 text-gray-700">
                            The check engine light is one of the most common warning lights, but it is
                            also one of the most misunderstood. It can come on for many reasons,
                            ranging from something relatively simple to something that needs immediate
                            attention.
                        </p>
                        <p className="mb-4 text-gray-700">
                            In some cases, the vehicle may still feel normal. In others, you may
                            notice rough idling, poor acceleration, reduced fuel economy, or engine
                            misfires. A steady check engine light usually means the problem should be
                            inspected soon. A flashing check engine light is more urgent and often
                            means you should stop driving as soon as it is safe.
                        </p>
                        <p className="m-0 text-gray-700">
                            The check engine light should never be ignored for long, especially if the
                            car is running poorly or the light is flashing.
                        </p>
                    </section>

                    <section id="light-2" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            2. Oil Pressure Warning Light
                        </h2>
                        <p className="mb-4 text-gray-700">
                            This is one of the most serious warning lights you can see. The oil
                            pressure warning light usually means the engine may not be getting enough
                            oil pressure to properly lubricate moving parts.
                        </p>
                        <p className="mb-4 text-gray-700">
                            Driving with low oil pressure can cause severe engine damage very quickly.
                            The issue could be low oil level, a failing oil pump, an oil leak, or
                            another lubrication problem.
                        </p>
                        <p className="m-0 text-gray-700">
                            If this light appears while driving, it is usually safest to pull over,
                            turn the engine off, and inspect the situation before continuing.
                        </p>
                    </section>

                    <section id="light-3" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            3. Battery Warning Light
                        </h2>
                        <p className="mb-4 text-gray-700">
                            Many drivers assume the battery warning light only means the battery is
                            weak, but that is not always true. In many cases, it actually points to a
                            charging system problem.
                        </p>
                        <p className="mb-4 text-gray-700">
                            The issue could involve the alternator, battery terminals, drive belt,
                            wiring, or battery itself. If the charging system stops working properly,
                            the car may continue running for a short time but eventually lose
                            electrical power.
                        </p>
                        <p className="m-0 text-gray-700">
                            If this light comes on, especially together with dim lights or electrical
                            issues, it should be checked as soon as possible.
                        </p>
                    </section>

                    <section id="light-4" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            4. Brake Warning Light
                        </h2>
                        <p className="mb-4 text-gray-700">
                            The brake warning light matters because it may indicate a problem with one
                            of the most important safety systems in the vehicle. Sometimes it comes on
                            because the parking brake is still engaged, but it can also point to low
                            brake fluid or a brake system issue.
                        </p>
                        <p className="mb-4 text-gray-700">
                            If you notice this warning together with a soft brake pedal, unusual
                            noises, longer stopping distances, or pulling during braking, the problem
                            could be more serious.
                        </p>
                        <p className="m-0 text-gray-700">
                            A brake-related warning should never be dismissed without understanding why
                            it appeared.
                        </p>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/5_3.jpeg"
                            alt="Dashboard brake and temperature warning lights"
                            title="Brake and engine temperature warning lights"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: dashboard_brake_temp_lights.png
                        </figcaption>
                    </figure>

                    <section id="light-5" className="mb-11 pt-[1px]">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            5. Engine Temperature Warning Light
                        </h2>
                        <p className="mb-4 text-gray-700">
                            The engine temperature warning light usually means the engine is
                            overheating or close to overheating. This can happen because of low
                            coolant, a leak, a failing thermostat, radiator issues, a bad fan, or
                            other cooling system problems.
                        </p>
                        <p className="mb-4 text-gray-700">
                            Continuing to drive while the engine is overheating can cause major
                            damage, including warped components or head gasket failure. That is why
                            this light should always be taken seriously.
                        </p>
                        <p className="m-0 text-gray-700">
                            If the temperature warning comes on, it is usually safest to stop the
                            vehicle when possible and allow the engine to cool before checking
                            anything.
                        </p>
                    </section>

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                Understand Warning Lights with CarFido
                            </h2>

                            <p className="mb-3.5 text-gray-700">
                                Dashboard warnings can feel stressful, especially when you are not sure
                                what the symbol means or whether it is safe to keep driving.
                            </p>

                            <p className="mb-3.5 text-gray-700">
                                CarFido helps drivers understand common warning lights, think through the
                                likely causes, and decide what next step makes sense based on symptoms
                                and urgency.
                            </p>

                            <p className="mb-2.5 text-gray-900 font-bold">Drivers can ask questions such as:</p>

                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li className="mb-1">What does this warning light mean?</li>
                                <li className="mb-1">Is it safe to drive with my battery light on?</li>
                            </ul>
                        </div>
                    </section>

                    <section id="extra-tips" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            What to Do When a Warning Light Appears
                        </h2>

                        <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700 mb-6">
                            <li>Do not panic, but do not ignore it either</li>
                            <li>Notice whether the car feels normal or not</li>
                            <li>Check if the light is steady or flashing</li>
                            <li>Pay attention to noises, smells, overheating, or poor braking</li>
                            <li>Pull over safely if the issue may affect safety or engine health</li>
                            <li>Schedule inspection quickly if the problem is not immediately dangerous</li>
                        </ul>

                        <p className="m-0 text-gray-700">
                            The key is not just knowing what the symbol looks like, but understanding
                            how urgently you need to respond.
                        </p>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>

                        <div className="space-y-1">
                            <FAQitem
                                question="Which dashboard warning lights are most serious?"
                                answer="Some of the most serious warning lights include the oil pressure warning light, engine temperature warning light, brake warning light, battery warning light, and check engine light."
                            />
                            <FAQitem
                                question="Can I keep driving with a dashboard warning light on?"
                                answer="It depends on the light. Oil pressure, overheating, and brake warnings are especially important and may require you to stop driving as soon as it is safe."
                            />
                            <FAQitem
                                question="What should I do if my check engine light comes on?"
                                answer="If the check engine light is steady and the car feels normal, have it checked soon. If it is flashing, stop driving and get the problem inspected immediately."
                            />
                            <FAQitem
                                question="Is a battery warning light the same as a bad battery?"
                                answer="Not always. A battery warning light can indicate a charging system issue involving the alternator, battery, wiring, or connections."
                            />
                            <FAQitem
                                question="Can CarFido help me understand warning lights?"
                                answer="Yes. CarFido can help explain warning lights, suggest practical next steps, and help drivers understand whether a problem may be urgent."
                            />
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>

                        <p className="mb-4 text-gray-700">
                            Dashboard warning lights are easy to ignore when the car still seems to be
                            driving normally, but some of them are giving you an important chance to
                            act before the problem gets worse.
                        </p>

                        <p className="mb-4 text-gray-700">
                            The check engine light, oil pressure warning, battery warning, brake
                            warning, and temperature warning light all deserve serious attention
                            because they can affect safety, drivability, or major mechanical
                            components.
                        </p>

                        <p className="m-0 text-gray-700">
                            The next time one of these lights appears, take a moment to understand
                            what it means instead of hoping it disappears. That simple step can save
                            you money, time, and stress.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">
                            Quick Navigation
                        </div>

                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#warning-table" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Warning summary</a></li>
                            <li><a href="#why-it-matters" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Why it matters</a></li>
                            <li><a href="#light-1" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Check engine</a></li>
                            <li><a href="#light-2" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Oil pressure</a></li>
                            <li><a href="#light-3" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Battery</a></li>
                            <li><a href="#light-4" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Brakes</a></li>
                            <li><a href="#light-5" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Temperature</a></li>
                            <li><a href="#extra-tips" className="no-underline text-gray-600 hover:text-gray-900 text-sm">What to do next</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">
                            Related Reads
                        </div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== 5);
                            const startIndex = 5 % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link 
                                key={relatedBlog.id}
                                to={relatedBlog.path} 
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog5;